import { useEffect, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/hooks/use-toast'

export type ProfileForm = {
  full_name: string
  bio: string
  headline: string
  contact_visible_everyone: boolean
  profile_public: boolean
  beats_raw: string
  phone: string
  nationality: string
  social_x: string
  social_linkedin: string
  social_instagram: string
  next_of_kin: string
  emergency_contact: string
  national_id_number: string
}

const EMPTY_FORM: ProfileForm = {
  full_name: '', bio: '', headline: '',
  contact_visible_everyone: false,
  profile_public: false, beats_raw: '', phone: '', nationality: '',
  social_x: '', social_linkedin: '', social_instagram: '',
  next_of_kin: '', emergency_contact: '', national_id_number: '',
}

export function useJournalistProfile() {
  const { toast } = useToast()
  const qc = useQueryClient()

  const [authUserId, setAuthUserId] = useState<string | null>(null)
  const [authEmail, setAuthEmail] = useState<string | null>(null)
  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [uploadingAvatar, setUploadingAvatar] = useState(false)
  const [uploadingDoc, setUploadingDoc] = useState<string | null>(null)
  const [form, setForm] = useState<ProfileForm>(EMPTY_FORM)

  const { data: profile, isLoading } = useQuery({
    queryKey: ['journalist-profile-full'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return null
      setAuthUserId(user.id)
      setAuthEmail(user.email ?? null)
      const { data } = await (supabase
        .from('journalist_profiles')
        .select('full_name, bio, headline, contact_visible, contact_visible_everyone, account_type, avatar_url, doc_national_id, doc_press_card, doc_assignment, slug, profile_public, beats, phone, nationality, social_x, social_linkedin, social_instagram, outlet_name, outlet_logo_url, outlet_verified, next_of_kin, emergency_contact, national_id_number, photo_status, photo_review_notes')
        .eq('id', user.id)
        .single() as any)
      return data as {
        full_name: string; bio: string; headline?: string; contact_visible: boolean; contact_visible_everyone: boolean; account_type: string
        avatar_url?: string; doc_national_id?: string; doc_press_card?: string; doc_assignment?: string
        slug?: string; profile_public?: boolean; beats?: string[]; phone?: string; nationality?: string
        social_x?: string; social_linkedin?: string; social_instagram?: string
        outlet_name?: string; outlet_logo_url?: string; outlet_verified?: boolean
        next_of_kin?: string; emergency_contact?: string; national_id_number?: string
        photo_status?: 'pending' | 'approved' | 'reupload_requested'; photo_review_notes?: string
      } | null
    },
  })

  const { data: score } = useQuery({
    queryKey: ['journalist-profile-score'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return null
      const { data } = await (supabase
        .from('journalist_coverage_scores' as any)
        .select('score_total, badge_tier, article_count, org_count')
        .eq('journalist_id', user.id)
        .maybeSingle() as any)
      return data as any | null
    },
  })

  const { data: accreditations } = useQuery({
    queryKey: ['my-accreditation-history'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return []
      const { data } = await (supabase
        .from('journalist_accreditations' as any)
        .select('id, status, accredited_at, organizations(name)')
        .eq('journalist_id', user.id)
        .order('accredited_at', { ascending: false }) as any)
      return (data ?? []) as any[]
    },
  })

  const { data: scoreBreakdown } = useQuery({
    queryKey: ['my-score-breakdown'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_my_score_breakdown' as any)
      if (error) return []
      return (data ?? []) as Array<{ component: string; score: number; max_score: number; description: string }>
    },
  })

  const { data: submissions } = useQuery({
    queryKey: ['my-submission-history'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return []
      const { data } = await supabase
        .from('journalist_submissions')
        .select('id, content_url, submitted_at, similarity_score, organizations(name)')
        .eq('journalist_id', user.id)
        .order('submitted_at', { ascending: false })
      return (data ?? []) as any[]
    },
  })

  const { data: overallRanking } = useQuery({
    queryKey: ['my-overall-ranking'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return null
      const { data } = await (supabase
        .from('journalist_overall_rankings' as any)
        .select('overall_rank, total_journalists, score_total, badge_tier')
        .eq('journalist_id', user.id)
        .maybeSingle() as any)
      return data as { overall_rank: number; total_journalists: number; score_total: number; badge_tier: string } | null
    },
  })

  const { data: orgRankings = [] } = useQuery({
    queryKey: ['my-org-rankings'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return []
      const { data } = await (supabase
        .from('journalist_org_rankings' as any)
        .select('org_id, org_rank, org_journalist_count, score_total, article_count, org_name, org_logo_url')
        .eq('journalist_id', user.id)
        .order('org_rank') as any)
      return (data ?? []) as any[]
    },
  })

  const { data: crmProfile } = useQuery({
    queryKey: ['portal-profile-crm-bridge'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user?.email) return null
      const { data } = await (supabase
        .from('journalists' as any)
        .select('position, verification_id, press_card_status, organizations(name)')
        .eq('email', user.email)
        .maybeSingle() as any)
      return data as any | null
    },
  })

  const resetForm = () => {
    if (!profile) return
    setForm({
      full_name: profile.full_name ?? '',
      bio: profile.bio ?? '',
      headline: profile.headline ?? '',
      contact_visible_everyone: profile.contact_visible_everyone ?? false,
      profile_public: profile.profile_public ?? false,
      beats_raw: (profile.beats ?? []).join(', '),
      phone: profile.phone ?? '',
      nationality: profile.nationality ?? '',
      social_x: profile.social_x ?? '',
      social_linkedin: profile.social_linkedin ?? '',
      social_instagram: profile.social_instagram ?? '',
      next_of_kin: profile.next_of_kin ?? '',
      emergency_contact: profile.emergency_contact ?? '',
      national_id_number: profile.national_id_number ?? '',
    })
  }

  useEffect(() => { resetForm() /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [profile])

  const dirty = !!profile && (
    form.full_name !== (profile.full_name ?? '') ||
    form.bio !== (profile.bio ?? '') ||
    form.headline !== (profile.headline ?? '') ||
    form.contact_visible_everyone !== (profile.contact_visible_everyone ?? false) ||
    form.profile_public !== (profile.profile_public ?? false) ||
    form.beats_raw !== (profile.beats ?? []).join(', ') ||
    form.phone !== (profile.phone ?? '') ||
    form.nationality !== (profile.nationality ?? '') ||
    form.social_x !== (profile.social_x ?? '') ||
    form.social_linkedin !== (profile.social_linkedin ?? '') ||
    form.social_instagram !== (profile.social_instagram ?? '') ||
    form.next_of_kin !== (profile.next_of_kin ?? '') ||
    form.emergency_contact !== (profile.emergency_contact ?? '') ||
    form.national_id_number !== (profile.national_id_number ?? '')
  )

  const invalidateProfile = () => {
    qc.invalidateQueries({ queryKey: ['journalist-profile-full'] })
    qc.invalidateQueries({ queryKey: ['journalist-profile'] })
  }

  const save = async () => {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return
    setSaving(true)
    try {
      const beats = form.beats_raw.split(',').map(b => b.trim()).filter(Boolean)
      const { error } = await (supabase
        .from('journalist_profiles')
        .update({
          full_name: form.full_name,
          bio: form.bio,
          headline: form.headline || null,
          contact_visible_everyone: form.contact_visible_everyone,
          profile_public: form.profile_public,
          beats,
          phone: form.phone || null,
          nationality: form.nationality || null,
          social_x: form.social_x || null,
          social_linkedin: form.social_linkedin || null,
          social_instagram: form.social_instagram || null,
          next_of_kin: form.next_of_kin || null,
          emergency_contact: form.emergency_contact || null,
          national_id_number: form.national_id_number || null,
        })
        .eq('id', user.id) as any)
      if (error) throw error
      toast({ title: 'Profile updated.' })
      setEditing(false)
      invalidateProfile()
    } catch (err: any) {
      if (err?.code === '23505') {
        toast({ title: 'That ID number is already registered', description: 'Each journalist may only hold one account. If this is your document, contact support instead of creating a new profile.', variant: 'destructive' })
      } else {
        toast({ title: 'Failed to save profile.', variant: 'destructive' })
      }
    } finally {
      setSaving(false)
    }
  }

  const cancelEdit = () => { resetForm(); setEditing(false) }

  const uploadAvatar = async (file: File) => {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return
    setUploadingAvatar(true)
    try {
      const ext = file.name.split('.').pop()
      const path = `journalist/${user.id}/${Date.now()}.${ext}`
      const { error: uploadError } = await supabase.storage.from('avatars').upload(path, file, { upsert: true })
      if (uploadError) throw uploadError
      const { data: { publicUrl } } = supabase.storage.from('avatars').getPublicUrl(path)
      const { error: updateError } = await (supabase.from('journalist_profiles').update({ avatar_url: publicUrl }).eq('id', user.id) as any)
      if (updateError) throw updateError
      toast({ title: 'Photo updated.', description: 'Submitted for KYC review by your accrediting institution.' })
      invalidateProfile()
    } catch (err: any) {
      toast({ title: 'Upload failed', description: err.message, variant: 'destructive' })
    } finally {
      setUploadingAvatar(false)
    }
  }

  const uploadDoc = async (key: string, file: File) => {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return
    setUploadingDoc(key)
    try {
      const ext = file.name.split('.').pop()
      const path = `${user.id}/${key}/${Date.now()}.${ext}`
      const { error: uploadError } = await supabase.storage.from('journalist-docs').upload(path, file, { upsert: true })
      if (uploadError) throw uploadError
      await (supabase.from('journalist_profiles') as any).update({ [key]: path }).eq('id', user.id)
      toast({ title: 'Document uploaded.' })
      qc.invalidateQueries({ queryKey: ['journalist-profile-full'] })
    } catch (err: any) {
      toast({ title: 'Upload failed', description: err.message, variant: 'destructive' })
    } finally {
      setUploadingDoc(null)
    }
  }

  const viewDoc = async (path: string) => {
    const { data } = await supabase.storage.from('journalist-docs').createSignedUrl(path, 120)
    if (data?.signedUrl) window.open(data.signedUrl, '_blank')
  }

  return {
    profile, isLoading, score, accreditations, scoreBreakdown, submissions, crmProfile,
    overallRanking, orgRankings,
    authUserId, authEmail,
    form, setForm, dirty, editing, setEditing, saving, save, cancelEdit,
    uploadingAvatar, uploadAvatar, uploadingDoc, uploadDoc, viewDoc,
  }
}

export type JournalistProfileState = ReturnType<typeof useJournalistProfile>
